import React from 'react';
import BuildingAppearanceFields from '../BuildingAppearanceFields';
import type { Service } from '../../../../../../shared/types/config';

export interface BuildingTypeConfigItem {
  id: number;
  code: string;
  service_id: number;
  nom: string;
  icon: string | null;
  couleur: string | null;
  ordre: number;
  prix: number;
  actif: number;
  created_at?: string;
}

interface BatimentsTabProps {
  t: (key: string) => string;
  loading: boolean;
  services: Service[];
  buildingForm: {
    code: string;
    nom: string;
    icon: string;
    couleur: string;
    ordre: number;
    actif: number;
    service_id: number;
    prix: number;
  };
  setBuildingForm: React.Dispatch<React.SetStateAction<{
    code: string;
    nom: string;
    icon: string;
    couleur: string;
    ordre: number;
    actif: number;
    service_id: number;
    prix: number;
  }>>;
  buildingTypesConfig: BuildingTypeConfigItem[];
  editingBuildingId: number | null;
  savingBuilding: boolean;
  handleSaveBuilding: () => void;
  handleCancelEditBuilding: () => void;
  handleEditBuilding: (b: BuildingTypeConfigItem) => void;
  handleDeleteBuilding: (id: number) => void;
}

const inputCls = 'w-full rounded-lg border border-[#223046] bg-[#0b1220] px-3 py-2 text-sm text-cockpit-text placeholder-cockpit-muted focus:outline-none focus:border-[#6366f1]/60';
const labelCls = 'block text-xs font-medium text-cockpit-text-2 mb-1.5';

const BatimentsTab: React.FC<BatimentsTabProps> = ({
  t,
  loading,
  services,
  buildingForm,
  setBuildingForm,
  buildingTypesConfig,
  editingBuildingId,
  savingBuilding,
  handleSaveBuilding,
  handleCancelEditBuilding,
  handleEditBuilding,
  handleDeleteBuilding,
}) => (
  <div className="space-y-6">
    <p className="text-sm text-cockpit-text-2">{t('batiments.description')}</p>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className={labelCls}>{t('batiments.codeLabel')}</label>
        <input
          type="text"
          value={buildingForm.code}
          onChange={(e) => setBuildingForm((f) => ({ ...f, code: e.target.value }))}
          placeholder={t('batiments.codePlaceholder')}
          className={inputCls}
          disabled={!!editingBuildingId}
        />
      </div>
      <div>
        <label className={labelCls}>{t('batiments.nomLabel')}</label>
        <input
          type="text"
          value={buildingForm.nom}
          onChange={(e) => setBuildingForm((f) => ({ ...f, nom: e.target.value }))}
          placeholder={t('batiments.nomPlaceholder')}
          className={inputCls}
        />
      </div>
      <div>
        <label className={labelCls}>{t('batiments.serviceLabel')}</label>
        <select
          value={buildingForm.service_id}
          onChange={(e) => setBuildingForm((f) => ({ ...f, service_id: parseInt(e.target.value, 10) || 1 }))}
          className={inputCls}
        >
          {services.map((s) => (
            <option key={s.id} value={s.id}>{s.nom}</option>
          ))}
        </select>
        <p className="text-xs text-cockpit-muted mt-1">{t('batiments.serviceHint')}</p>
      </div>
      <div>
        <label className={labelCls}>{t('batiments.prixLabel')}</label>
        <input
          type="number"
          min={0}
          step={0.01}
          value={Number.isFinite(buildingForm.prix) ? buildingForm.prix : 0}
          onChange={(e) => setBuildingForm((f) => ({ ...f, prix: Math.max(0, parseFloat(e.target.value) || 0) }))}
          className={inputCls}
        />
      </div>
      <BuildingAppearanceFields
        icon={buildingForm.icon}
        couleur={buildingForm.couleur}
        onIconChange={(icon) => setBuildingForm((f) => ({ ...f, icon }))}
        onCouleurChange={(couleur) => setBuildingForm((f) => ({ ...f, couleur }))}
        labels={{
          iconLabel: t('batiments.iconLabel'),
          iconPlaceholder: t('batiments.iconPlaceholder'),
          iconPickHint: t('batiments.iconPickHint'),
          couleurLabel: t('batiments.couleurLabel'),
          couleurPlaceholder: t('batiments.couleurPlaceholder'),
          colorPickHint: t('batiments.colorPickHint'),
          customTailwind: t('batiments.customTailwind'),
        }}
      />
      <div>
        <label className={labelCls}>{t('batiments.ordreLabel')}</label>
        <input
          type="number"
          min={0}
          value={buildingForm.ordre}
          onChange={(e) => setBuildingForm((f) => ({ ...f, ordre: parseInt(e.target.value, 10) || 0 }))}
          className={inputCls}
        />
      </div>
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="building-actif"
          checked={!!buildingForm.actif}
          onChange={(e) => setBuildingForm((f) => ({ ...f, actif: e.target.checked ? 1 : 0 }))}
          className="rounded border-[#223046] bg-[#0b1220]"
        />
        <label htmlFor="building-actif" className="text-sm text-cockpit-text-2">{t('batiments.actifLabel')}</label>
      </div>
    </div>
    <div className="flex gap-2">
      <button
        type="button"
        onClick={handleSaveBuilding}
        disabled={savingBuilding}
        className="rounded-lg border border-[#6366f1]/40 bg-[#6366f1]/20 px-4 py-2 text-sm font-medium text-[#c7d2fe] hover:bg-[#6366f1]/30 transition-colors disabled:opacity-50"
      >
        {savingBuilding ? t('batiments.saving') : editingBuildingId ? t('batiments.update') : t('batiments.add')}
      </button>
      {editingBuildingId && (
        <button
          type="button"
          onClick={handleCancelEditBuilding}
          className="rounded-lg border border-cockpit-line/60 bg-cockpit-bg-2/30 px-4 py-2 text-sm font-medium text-cockpit-text hover:bg-cockpit-bg-2/45 transition-colors"
        >
          {t('batiments.cancel')}
        </button>
      )}
    </div>
    <div className="border-t border-[#1a2537] pt-4">
      <h2 className="text-sm font-semibold text-cockpit-text mb-3">{t('batiments.listTitle')}</h2>
      {loading ? (
        <p className="text-sm text-cockpit-muted">{t('batiments.loading')}</p>
      ) : buildingTypesConfig.length === 0 ? (
        <p className="text-sm text-cockpit-muted">{t('batiments.empty')}</p>
      ) : (
        <ul className="divide-y divide-[#1a2537]">
          {buildingTypesConfig.map((b) => (
            <li key={b.id} className="py-2 flex items-center justify-between gap-4">
              <span className="text-2xl">{b.icon || '🏢'}</span>
              <span className={`flex-1 ${b.actif ? b.couleur || 'text-cockpit-text' : 'text-cockpit-muted'}`}>
                {b.nom} <span className="text-cockpit-muted">({b.code})</span>
                {!b.actif && <span className="ml-2 text-xs text-amber-400">{t('batiments.inactif')}</span>}
              </span>
              <span className="text-xs text-cockpit-muted">
                {t('batiments.ordre')}: {b.ordre} · {t('batiments.prixShort')}{' '}
                {typeof b.prix === 'number' ? b.prix.toFixed(2) : Number(b.prix || 0).toFixed(2)} €
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handleEditBuilding(b)}
                  className="rounded border border-cockpit-line/60 bg-cockpit-bg-2/30 px-2 py-1 text-xs font-medium text-cockpit-text hover:bg-cockpit-bg-2/45 transition-colors"
                >
                  {t('batiments.modify')}
                </button>
                <button
                  type="button"
                  onClick={() => handleDeleteBuilding(b.id)}
                  className="rounded border border-red-500/35 bg-red-500/10 px-2 py-1 text-xs font-medium text-red-300 hover:bg-red-500/20 transition-colors"
                >
                  {t('batiments.delete')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  </div>
);

export default BatimentsTab;
